import React from 'react';
import { useMutation } from '@apollo/client';
import ScanLabelMutation from './ScanLabelMutation';

/**
 * Controller for the scan-label request currently in flight, held at module scope rather than in a
 * ref.
 *
 * Only one label scan can be in flight at a time, and the component that needs to cancel it is not
 * necessarily the one that started it: the tracker renders through SlideRoutes with
 * `destroy={false}`, so an older instance of the analysis page can still be mounted and be the one
 * handling the back navigation. With a per-instance ref, that instance held `null` and the abort
 * silently did nothing — the request ran to completion and its result opened the add-product form on
 * top of the screen the user had gone back to.
 */
let inFlight: AbortController | null = null;

/**
 * Whether a scan-label request is currently in flight.
 *
 * Module-scoped for the same reason as `inFlight`: the instance handling a back navigation is not
 * necessarily the instance that started the request (SlideRoutes keeps the previous one mounted), so
 * a per-instance flag read `false` and suppressed the abort entirely.
 */
export const isScanLabelInFlight = () => inFlight !== null;

interface IScanLabelConnectorApolloProps {
  children: ({
    scanLabel,
    cancelScanLabel,
  }: {
    scanLabel: (images: File[], userComment?: string) => Promise<unknown>;
    cancelScanLabel: () => void;
  }) => React.ReactElement;
}

const ScanLabelConnectorApollo: React.FC<IScanLabelConnectorApolloProps> = ({ children }) => {
  const [scanLabelMutation] = useMutation(ScanLabelMutation);

  const scanLabel = async (images: File[], userComment?: string) => {
    const controller = new AbortController();
    inFlight = controller;

    try {
      const response = await scanLabelMutation({
        variables: { images, userComment },
        context: {
          fetchOptions: {
            signal: controller.signal,
          },
        },
      });

      return response.data?.dailyTracker?.scanLabel;
    } finally {
      // Only clear it if a later scan has not already taken over.
      if (inFlight === controller) inFlight = null;
    }
  };

  const cancelScanLabel = () => {
    if (inFlight) {
      inFlight.abort();
      inFlight = null;
    }
  };

  return children({ scanLabel, cancelScanLabel });
};

export default ScanLabelConnectorApollo;
